import { getIdpBaseUrl } from '../api';

/**
 * Release-notes subscribers (double opt-in) — mirrors dcc-web's `idpPortalApiSlice`
 * `/releases/subscriptions` endpoints. Admins add customer contacts here; each gets a
 * confirmation email and only CONFIRMED contacts receive release-note emails.
 *
 * NOTE: unlike `fetchCustomerReleaseFeed`, the idp-portal backend only accepts these
 * calls with an Auth0 access token scoped to the idp-portal API audience (not a plain
 * ID token) — pass that token as `token` or these calls will 401.
 */

export type SubscriptionStatus = 'pending' | 'confirmed' | 'unsubscribed';

export type ReleaseSubscription = {
  id: string;
  customer: string;
  tenant_id?: string | null;
  email: string;
  status: SubscriptionStatus;
  created_at: string;
  confirmed_at?: string | null;
};

type BaseCallParams = {
  /** Auth0 access token scoped to the idp-portal API audience. */
  token?: string;
  /** Overrides `getIdpBaseUrl()` / `CTY_IDP_API_URL` for this call. */
  baseUrl?: string;
  signal?: AbortSignal;
};

const SUBSCRIPTIONS_PATH = '/api/v1/releases/subscriptions';

function stripTrailingSlashes(value: string): string {
  let end = value.length;
  while (end > 0 && value.charAt(end - 1) === '/') {
    end -= 1;
  }
  return value.slice(0, end);
}

function resolveIdpBase(baseUrl?: string): string {
  const base = stripTrailingSlashes((baseUrl ?? getIdpBaseUrl()).trim());
  if (!base) {
    throw new Error('IDP base URL is not configured. Set CTY_IDP_API_URL or call setIdpConfig({ baseUrl }).');
  }
  return base;
}

function authHeaders(token?: string): Record<string, string> {
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function readErrorMessage(response: Response): Promise<string> {
  const text = await response.text().catch(() => '');
  try {
    const parsed = JSON.parse(text);
    return parsed?.error?.message ?? parsed?.message ?? text;
  } catch {
    return text;
  }
}

export async function fetchReleaseSubscriptions(
  params: { customer?: string } & BaseCallParams
): Promise<ReleaseSubscription[]> {
  const base = resolveIdpBase(params.baseUrl);
  const query = params.customer ? `?${new URLSearchParams({ customer: params.customer }).toString()}` : '';
  const response = await fetch(`${base}${SUBSCRIPTIONS_PATH}${query}`, {
    method: 'GET',
    headers: authHeaders(params.token),
    signal: params.signal,
  });
  if (!response.ok) {
    throw new Error(`releases/subscriptions failed: ${response.status} ${await readErrorMessage(response)}`);
  }
  return (await response.json()) as ReleaseSubscription[];
}

export async function addReleaseSubscribers(
  params: { customer: string; emails: string[]; tenantId?: string } & BaseCallParams
): Promise<ReleaseSubscription[]> {
  const base = resolveIdpBase(params.baseUrl);
  const response = await fetch(`${base}${SUBSCRIPTIONS_PATH}`, {
    method: 'POST',
    headers: { ...authHeaders(params.token), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      customer: params.customer,
      emails: params.emails,
      ...(params.tenantId ? { tenant_id: params.tenantId } : {}),
    }),
    signal: params.signal,
  });
  if (!response.ok) {
    throw new Error(`add subscribers failed: ${response.status} ${await readErrorMessage(response)}`);
  }
  return (await response.json()) as ReleaseSubscription[];
}

export async function resendReleaseSubscription(
  params: { id: string } & BaseCallParams
): Promise<ReleaseSubscription> {
  const base = resolveIdpBase(params.baseUrl);
  const response = await fetch(`${base}${SUBSCRIPTIONS_PATH}/${encodeURIComponent(params.id)}/resend`, {
    method: 'POST',
    headers: authHeaders(params.token),
    signal: params.signal,
  });
  if (!response.ok) {
    throw new Error(`resend subscription failed: ${response.status} ${await readErrorMessage(response)}`);
  }
  return (await response.json()) as ReleaseSubscription;
}

export async function removeReleaseSubscription(
  params: { id: string } & BaseCallParams
): Promise<void> {
  const base = resolveIdpBase(params.baseUrl);
  const response = await fetch(`${base}${SUBSCRIPTIONS_PATH}/${encodeURIComponent(params.id)}`, {
    method: 'DELETE',
    headers: authHeaders(params.token),
    signal: params.signal,
  });
  if (!response.ok) {
    throw new Error(`remove subscription failed: ${response.status} ${await readErrorMessage(response)}`);
  }
}
